import { DEFAULT_LOCALE, getIntlLocaleTag } from "@/lib/locales";
import { getIntlLocale } from "@/i18n";
import {
  ianaTimezoneForRegionCode,
  partValue,
} from "@/lib/activityTimezone";
import type { RegionCode } from "@/types";

/** Locale that yields WEST/WET/CEST/CET (en-US often yields GMT±N). */
const TZ_ABBR_LOCALE = getIntlLocaleTag(DEFAULT_LOCALE);

function formatTimeHm(date: Date, timeZone: string, locale: string): string {
  const parts = new Intl.DateTimeFormat(locale, {
    timeZone,
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(date);
  return `${partValue(parts, "hour")}:${partValue(parts, "minute")}`;
}

function timezoneAbbreviation(date: Date, timeZone: string): string {
  const parts = new Intl.DateTimeFormat(TZ_ABBR_LOCALE, {
    timeZone,
    timeZoneName: "short",
  }).formatToParts(date);
  return partValue(parts, "timeZoneName") || timeZone;
}

export type ActivityDateTimeLabels = {
  dateLabel: string;
  /** Compact date for dense rows (e.g. "Fri, Oct 30"). */
  shortDateLabel: string;
  timeLabel: string;
  fullLabel: string;
};

export type FormatActivityDateTimeOptions = {
  /** Partner portal: HH:mm only, no timezone abbrev or browser-local suffix. */
  timeOnly?: boolean;
};

/**
 * Format an absolute UTC instant in the activity payment-region timezone.
 * Returns null when the activity region is unknown (no UTC fallback).
 * Appends browser-local time only when the runtime exposes a real IANA zone.
 */
export function formatActivityDateTime(
  iso: string,
  regionCode: RegionCode,
  localTimeSuffix: (time: string) => string = () => "",
  options?: FormatActivityDateTimeOptions,
): ActivityDateTimeLabels | null {
  const activityTz = ianaTimezoneForRegionCode(regionCode);
  if (!activityTz) {
    return null;
  }

  const date = new Date(iso);
  const locale = getIntlLocale();

  const dateLabel = date.toLocaleDateString(locale, {
    timeZone: activityTz,
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  const shortDateLabel = date.toLocaleDateString(locale, {
    timeZone: activityTz,
    weekday: "short",
    day: "numeric",
    month: "short",
  });

  const activityTime = formatTimeHm(date, activityTz, locale);

  const fullBase = date.toLocaleString(locale, {
    timeZone: activityTz,
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });

  if (options?.timeOnly) {
    return {
      dateLabel,
      shortDateLabel,
      timeLabel: activityTime,
      fullLabel: fullBase,
    };
  }

  const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const abbr = timezoneAbbreviation(date, activityTz);

  let localNote = "";
  if (browserTz && browserTz !== activityTz) {
    const localTime = formatTimeHm(date, browserTz, locale);
    if (activityTime !== localTime) {
      localNote = ` ${localTimeSuffix(localTime)}`;
    }
  }

  return {
    dateLabel,
    shortDateLabel,
    timeLabel: `${activityTime} ${abbr}${localNote}`,
    fullLabel: `${fullBase} ${abbr}${localNote}`,
  };
}
